import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Phone, PhoneOff, Play, Pause, SkipForward, CheckCircle2, XCircle,
  Loader2, Trash2, ListChecks, Clock, PhoneOutgoing, Voicemail,
  Zap, Lock, BarChart3, MessageSquare, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import ClientLocalTime from './ClientLocalTime';

const OUTCOMES = [
  { value: 'no_answer', label: 'No Answer', icon: PhoneOff, color: 'text-slate-600 bg-slate-50 border-slate-200 hover:bg-slate-100' },
  { value: 'voicemail', label: 'Voicemail Left', icon: Voicemail, color: 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100' },
  { value: 'spoke_not_interested', label: 'Spoke - Not Interested', icon: XCircle, color: 'text-red-700 bg-red-50 border-red-200 hover:bg-red-100' },
  { value: 'spoke_interested', label: 'Spoke - Interested', icon: CheckCircle2, color: 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100' },
  { value: 'call_back', label: 'Call Back', icon: Clock, color: 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100' },
];

const statusColors = {
  pending: 'bg-slate-100 text-slate-600',
  calling: 'bg-blue-100 text-blue-700 animate-pulse',
  completed: 'bg-emerald-100 text-emerald-700',
};

const outcomeLabels = {
  no_answer: 'No Answer', voicemail: 'Voicemail',
  spoke_not_interested: 'Not Interested', spoke_interested: 'Interested',
  call_back: 'Call Back', skipped: 'Skipped',
};

const ctxLabel = (ctx) => ctx === 'ptc_travel' ? 'PTC Travel' : 'EQF / PTC Hub';
const ctxNumber = (ctx) => ctx === 'ptc_travel' ? '+14073057738' : '+17574483705';

export default function SmartDialer() {
  const [queues, setQueues] = useState([]);
  const [activeQueue, setActiveQueue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calling, setCalling] = useState(false);
  const [showOutcome, setShowOutcome] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [droppingVm, setDroppingVm] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [companyContext, setCompanyContext] = useState('eqf_hub');
  const [clientSearch, setClientSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [searching, setSearching] = useState(false);
  const [autoNext, setAutoNext] = useState(true);
  const [speedMode, setSpeedMode] = useState(false);
  const searchTimer = useRef(null);

  const fetchQueues = useCallback(async () => {
    try {
      const res = await api.get('/telephony/dial-queues');
      setQueues(res.data.queues || []);
    } catch { /* silent */ }
  }, []);

  const fetchQueue = useCallback(async (queueId) => {
    try {
      const res = await api.get(`/telephony/dial-queue/${queueId}`);
      const q = res.data.queue;
      setActiveQueue(q);
      if (q.auto_next !== undefined) setAutoNext(q.auto_next);
      if (q.speed_mode !== undefined) setSpeedMode(q.speed_mode);
    } catch { toast.error('Failed to load queue'); }
  }, []);

  useEffect(() => { fetchQueues(); }, [fetchQueues]);

  // Client search
  const searchClients = async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/clients?search=${encodeURIComponent(q)}&limit=15`);
      const clients = res.data.clients || res.data || [];
      setSearchResults(clients.filter(c => c.phone && !selectedClients.find(s => s.id === c.id)));
    } catch { /* silent */ }
    finally { setSearching(false); }
  };

  const handleSearchInput = (val) => {
    setClientSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchClients(val), 300);
  };

  const addClient = (client) => {
    if (!selectedClients.find(c => c.id === client.id)) {
      setSelectedClients(prev => [...prev, { id: client.id, full_name: client.full_name, phone: client.phone }]);
    }
    setSearchResults(prev => prev.filter(c => c.id !== client.id));
  };

  const removeClient = (id) => setSelectedClients(prev => prev.filter(c => c.id !== id));

  const createQueue = async () => {
    if (selectedClients.length === 0) { toast.error('Add at least one client'); return; }
    setLoading(true);
    try {
      const res = await api.post('/telephony/dial-queue', {
        client_ids: selectedClients.map(c => c.id),
        company_context: companyContext,
        auto_next: autoNext,
        speed_mode: speedMode,
      });
      toast.success(`Queue created with ${res.data.queue.total_leads} leads`);
      setShowCreate(false);
      setSelectedClients([]);
      setClientSearch('');
      fetchQueues();
      fetchQueue(res.data.queue.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create queue');
    } finally { setLoading(false); }
  };

  const dialNext = async () => {
    if (!activeQueue) return;
    setCalling(true);
    try {
      const res = await api.post(`/telephony/dial-queue/${activeQueue.id}/next`);
      if (res.data.status === 'completed') {
        toast.info('All leads dialed!');
        fetchQueue(activeQueue.id);
        setCalling(false);
        return;
      }
      toast.success(res.data.message);
      if (!speedMode) {
        setTimeout(() => { setShowOutcome(true); setCalling(false); }, 1500);
      } else {
        setShowOutcome(true);
        setCalling(false);
      }
      fetchQueue(activeQueue.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Call failed');
      setCalling(false);
    }
  };

  const submitOutcome = async () => {
    if (!selectedOutcome) { toast.error('Select an outcome'); return; }
    if (!activeQueue) return;
    setSubmitting(true);
    try {
      const res = await api.put(`/telephony/dial-queue/${activeQueue.id}/outcome`, {
        outcome: selectedOutcome,
        notes: outcomeNotes || null,
      });
      toast.success(`Logged: ${outcomeLabels[selectedOutcome]}`);
      if (res.data.sms_sent) toast.info('Auto-text sent');
      if (res.data.retry_at) toast.info('Retry scheduled in 2h');

      setShowOutcome(false);
      setSelectedOutcome(null);
      setOutcomeNotes('');
      fetchQueue(activeQueue.id);

      if (res.data.queue_status === 'completed') {
        toast.info('Queue completed!');
      } else if (autoNext && res.data.next_lead) {
        // Auto-advance: dial next automatically
        setTimeout(() => dialNext(), speedMode ? 300 : 1500);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record outcome');
    } finally { setSubmitting(false); }
  };

  const dropVoicemail = async () => {
    if (!activeQueue) return;
    setDroppingVm(true);
    try {
      await api.post(`/telephony/dial-queue/${activeQueue.id}/voicemail-drop`);
      toast.success('Voicemail dropped');
      fetchQueue(activeQueue.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Drop failed');
    } finally { setDroppingVm(false); }
  };

  const toggleSetting = async (field, val) => {
    if (!activeQueue) return;
    if (field === 'auto_next') setAutoNext(val);
    if (field === 'speed_mode') setSpeedMode(val);
    try {
      await api.put(`/telephony/dial-queue/${activeQueue.id}/settings`, { [field]: val });
    } catch { /* silent */ }
  };

  const pauseQueue = async () => {
    try { await api.put(`/telephony/dial-queue/${activeQueue.id}/pause`); toast.success('Paused'); fetchQueue(activeQueue.id); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const resumeQueue = async () => {
    try { await api.put(`/telephony/dial-queue/${activeQueue.id}/resume`); toast.success('Resumed'); fetchQueue(activeQueue.id); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const deleteQueue = async (queueId) => {
    try {
      await api.delete(`/telephony/dial-queue/${queueId}`);
      toast.success('Queue deleted');
      if (activeQueue?.id === queueId) setActiveQueue(null);
      fetchQueues();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const stats = activeQueue?.stats || {};
  const progress = activeQueue ? Math.round((activeQueue.completed_count / activeQueue.total_leads) * 100) : 0;
  const remaining = activeQueue ? activeQueue.leads?.filter(l => l.status === 'pending').length : 0;
  const callingLead = activeQueue?.leads?.find(l => l.status === 'calling');

  return (
    <div className="space-y-4" data-testid="smart-dialer">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
            <ListChecks className="h-4.5 w-4.5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Smart Dialer</h3>
            <p className="text-[11px] text-slate-400">High-performance outbound calling</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-queue-btn">
          <Phone className="h-3.5 w-3.5 mr-1.5" /> New Queue
        </Button>
      </div>

      {activeQueue ? (
        <>
          {/* ── Active Queue Panel ── */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="active-queue">
            {/* Queue header + company lock */}
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-sm font-semibold text-slate-800" data-testid="queue-company">
                    {ctxLabel(activeQueue.company_context)}
                  </span>
                  <span className="text-[10px] text-slate-400 font-mono">{ctxNumber(activeQueue.company_context)}</span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    activeQueue.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                    activeQueue.status === 'paused' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'
                  }`} data-testid="queue-status">{activeQueue.status}</span>
                </div>
                <div className="flex items-center gap-2">
                  {activeQueue.status === 'active' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={pauseQueue} data-testid="pause-btn">
                      <Pause className="h-3 w-3 mr-1" /> Pause
                    </Button>
                  )}
                  {activeQueue.status === 'paused' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={resumeQueue} data-testid="resume-btn">
                      <Play className="h-3 w-3 mr-1" /> Resume
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => deleteQueue(activeQueue.id)} data-testid="delete-queue-btn">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-5 mt-2">
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <Switch checked={autoNext} onCheckedChange={(v) => toggleSetting('auto_next', v)} data-testid="auto-next-toggle" />
                  <span className={autoNext ? 'font-medium text-indigo-700' : ''}>Auto-Next</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <Switch checked={speedMode} onCheckedChange={(v) => toggleSetting('speed_mode', v)} data-testid="speed-mode-toggle" />
                  <Zap className={`h-3 w-3 ${speedMode ? 'text-amber-500' : 'text-slate-300'}`} />
                  <span className={speedMode ? 'font-medium text-amber-700' : ''}>Speed Mode</span>
                </label>
              </div>

              {/* Progress */}
              <div className="flex items-center gap-3 mt-3">
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} data-testid="progress-bar" />
                </div>
                <span className="text-xs text-slate-500 whitespace-nowrap" data-testid="progress-text">
                  {activeQueue.completed_count}/{activeQueue.total_leads}
                </span>
              </div>
            </div>

            {/* ── Session Stats ── */}
            <div className="grid grid-cols-5 gap-0 border-b border-slate-100 bg-slate-50/50" data-testid="session-stats">
              {[
                { label: 'Calls', val: stats.calls_made || 0, icon: Phone, color: 'text-slate-700' },
                { label: 'Contacts', val: stats.contacts || 0, icon: MessageSquare, color: 'text-blue-600' },
                { label: 'No Answer', val: stats.no_answers || 0, icon: PhoneOff, color: 'text-slate-400' },
                { label: 'Interested', val: stats.interested || 0, icon: CheckCircle2, color: 'text-emerald-600' },
                { label: 'SMS Sent', val: stats.sms_sent || 0, icon: MessageSquare, color: 'text-purple-600' },
              ].map(s => (
                <div key={s.label} className="px-3 py-2.5 text-center border-r border-slate-100 last:border-0">
                  <div className={`text-lg font-bold ${s.color}`}>{s.val}</div>
                  <div className="text-[10px] text-slate-400">{s.label}</div>
                </div>
              ))}
            </div>

            {/* ── Live Call Panel / Dial Controls ── */}
            {activeQueue.status !== 'completed' && (
              <div className="p-4 bg-white border-b border-slate-100">
                {callingLead ? (
                  <div data-testid="live-call-panel">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center animate-pulse">
                          <PhoneOutgoing className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800" data-testid="current-lead-name">{callingLead.client_name}</p>
                          <p className="text-xs text-slate-400 font-mono">{callingLead.phone}</p>
                        </div>
                      </div>
                      <div className="text-right text-[10px] text-slate-400">
                        {callingLead.deal_status && <p>Deal: {callingLead.deal_status}</p>}
                        {callingLead.deal_stage && <p>Stage: {callingLead.deal_stage}</p>}
                      </div>
                    </div>
                    {callingLead.notes && (
                      <p className="text-[11px] text-slate-500 bg-slate-50 rounded-lg px-3 py-2 mb-3 line-clamp-2">{callingLead.notes}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => setShowOutcome(true)} className="flex-1" data-testid="log-outcome-btn">
                        Log Outcome
                      </Button>
                      <Button size="sm" variant="outline" onClick={dropVoicemail} disabled={droppingVm} className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="drop-vm-btn">
                        {droppingVm ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Voicemail className="h-3.5 w-3.5 mr-1" />}
                        Drop VM
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600">
                      {remaining > 0 ? `${remaining} lead${remaining > 1 ? 's' : ''} remaining` : 'No more leads'}
                    </p>
                    <Button
                      size="sm"
                      disabled={calling || activeQueue.status === 'paused' || remaining === 0}
                      onClick={dialNext}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="dial-next-btn"
                    >
                      {calling ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Phone className="h-3.5 w-3.5 mr-1.5" />}
                      {calling ? 'Dialing...' : activeQueue.completed_count === 0 ? 'Start Dialing' : 'Next Call'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* ── Lead List ── */}
            <div className="max-h-[250px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-slate-100 text-left">
                    <th className="px-3 py-2 text-[10px] font-medium text-slate-400 w-6">#</th>
                    <th className="px-3 py-2 text-[10px] font-medium text-slate-400">Client</th>
                    <th className="px-3 py-2 text-[10px] font-medium text-slate-400">Phone</th>
                    <th className="px-3 py-2 text-[10px] font-medium text-slate-400">Local Time</th>
                    <th className="px-3 py-2 text-[10px] font-medium text-slate-400">Status</th>
                    <th className="px-3 py-2 text-[10px] font-medium text-slate-400">Outcome</th>
                    <th className="px-3 py-2 text-[10px] font-medium text-slate-400 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {activeQueue.leads?.map((lead, i) => (
                    <tr key={lead.client_id || lead.phone || `lead-${i}`} className={`border-b border-slate-50 ${lead.status === 'calling' ? 'bg-blue-50/40' : ''}`} data-testid={`lead-row-${i}`}>
                      <td className="px-3 py-1.5 text-[10px] text-slate-400">{i + 1}</td>
                      <td className="px-3 py-1.5 text-xs font-medium text-slate-700">{lead.client_name}</td>
                      <td className="px-3 py-1.5 text-[11px] text-slate-400 font-mono">{lead.phone}</td>
                      <td className="px-3 py-1.5">
                        {/* v3.18-1290 — quiet-hours-aware local time chip
                            so agents see the client's clock before dialing. */}
                        {lead.client_id ? (
                          <ClientLocalTime clientId={lead.client_id} variant="chip" />
                        ) : (
                          <span className="text-[10px] text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${statusColors[lead.status] || 'bg-slate-100 text-slate-500'}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-[11px] text-slate-500">{lead.outcome ? outcomeLabels[lead.outcome] : '—'}</td>
                      <td className="px-3 py-1.5 flex items-center gap-1">
                        {lead.voicemail_dropped && <Voicemail className="h-3 w-3 text-amber-500" title="VM dropped" />}
                        {lead.sms_sent && <MessageSquare className="h-3 w-3 text-purple-500" title="SMS sent" />}
                        {lead.retry_at && <RotateCcw className="h-3 w-3 text-blue-400" title="Retry scheduled" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {activeQueue.status === 'completed' && (
              <div className="p-4 border-t border-slate-100 bg-emerald-50 text-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                <p className="text-sm font-medium text-emerald-800">Queue Complete</p>
                <p className="text-xs text-emerald-600 mt-0.5">
                  {stats.calls_made || 0} calls &middot; {stats.contacts || 0} contacts &middot; {stats.interested || 0} interested
                </p>
              </div>
            )}
          </div>

          <Button variant="ghost" size="sm" onClick={() => { setActiveQueue(null); fetchQueues(); }} data-testid="back-to-list">
            ← Back to queues
          </Button>
        </>
      ) : (
        /* ── Queue List ── */
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="p-4 border-b border-slate-100">
            <span className="text-sm font-medium text-slate-700">Your Dial Queues</span>
          </div>
          {queues.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              No dial queues yet. Create one to start dialing.
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {queues.map(q => (
                <div
                  key={q.id}
                  className="px-4 py-3 flex items-center justify-between hover:bg-slate-50/50 cursor-pointer"
                  onClick={() => fetchQueue(q.id)}
                  data-testid={`queue-item-${q.id}`}
                >
                  <div>
                    <p className="text-xs font-medium text-slate-700">
                      <Lock className="h-3 w-3 inline mr-1 text-slate-400" />
                      {ctxLabel(q.company_context)} — {q.total_leads} leads
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {q.completed_count}/{q.total_leads} completed
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      q.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                      q.status === 'paused' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'
                    }`}>{q.status}</span>
                    <button onClick={(e) => { e.stopPropagation(); deleteQueue(q.id); }} className="text-slate-300 hover:text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Create Queue Dialog ── */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[520px]" data-testid="create-queue-dialog">
          <DialogHeader>
            <DialogTitle>Create Dial Queue</DialogTitle>
            <DialogDescription>Company context is locked for the entire session.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Company Context (locked once created)</label>
              <Select value={companyContext} onValueChange={setCompanyContext}>
                <SelectTrigger className="h-9 text-sm" data-testid="queue-company-context">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="eqf_hub">EQF / PTC Hub — +17574483705</SelectItem>
                  <SelectItem value="ptc_travel">Prestigious Travel Club — +14073057738</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch checked={autoNext} onCheckedChange={setAutoNext} />
                Auto-Next (default ON)
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch checked={speedMode} onCheckedChange={setSpeedMode} />
                <Zap className="h-3 w-3 text-amber-500" /> Speed Mode
              </label>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Search Clients</label>
              <Input
                value={clientSearch}
                onChange={e => handleSearchInput(e.target.value)}
                placeholder="Search by name or phone..."
                className="h-9 text-sm"
                data-testid="client-search-input"
              />
              {searching && <p className="text-[10px] text-slate-400 mt-1">Searching...</p>}
              {searchResults.length > 0 && (
                <div className="mt-1 border border-slate-200 rounded-lg max-h-[130px] overflow-y-auto">
                  {searchResults.map(c => (
                    <button
                      key={c.id}
                      onClick={() => addClient(c)}
                      className="w-full px-3 py-2 text-left text-xs hover:bg-slate-50 flex justify-between border-b border-slate-50 last:border-0"
                      data-testid={`search-result-${c.id}`}
                    >
                      <span className="font-medium text-slate-700">{c.full_name}</span>
                      <span className="text-slate-400">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedClients.length > 0 && (
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Selected ({selectedClients.length})</label>
                <div className="flex flex-wrap gap-1.5 max-h-[100px] overflow-y-auto">
                  {selectedClients.map(c => (
                    <span key={c.id} className="inline-flex items-center gap-1 text-[11px] bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full">
                      {c.full_name}
                      <button onClick={() => removeClient(c.id)} className="text-indigo-400 hover:text-indigo-600">
                        <XCircle className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createQueue} disabled={loading || selectedClients.length === 0} data-testid="create-queue-submit">
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Create Queue ({selectedClients.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Outcome Dialog ── */}
      <Dialog open={showOutcome} onOpenChange={(open) => { if (!open && callingLead) { toast.error('Record an outcome first'); return; } setShowOutcome(open); }}>
        <DialogContent className="sm:max-w-[420px]" data-testid="outcome-dialog">
          <DialogHeader>
            <DialogTitle>Call Outcome</DialogTitle>
            <DialogDescription>
              {callingLead?.client_name || 'Unknown'} — {callingLead?.phone || ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2">
              {OUTCOMES.map(o => {
                const Icon = o.icon;
                const selected = selectedOutcome === o.value;
                return (
                  <button
                    key={o.value}
                    onClick={() => setSelectedOutcome(o.value)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${
                      selected ? 'ring-2 ring-indigo-400 border-indigo-300 bg-indigo-50' : o.color
                    }`}
                    data-testid={`outcome-${o.value}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm font-medium">{o.label}</span>
                    {selected && <CheckCircle2 className="h-4 w-4 ml-auto text-indigo-600" />}
                  </button>
                );
              })}
            </div>
            <Textarea
              value={outcomeNotes}
              onChange={e => setOutcomeNotes(e.target.value)}
              placeholder="Notes (optional)..."
              className="h-14 text-sm resize-none"
              data-testid="outcome-notes"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={submitOutcome}
              disabled={!selectedOutcome || submitting}
              className="w-full"
              data-testid="submit-outcome-btn"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <SkipForward className="h-3.5 w-3.5 mr-1.5" />}
              {submitting ? 'Saving...' : autoNext ? 'Log & Auto-Next' : 'Log Outcome'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
